import {
  AfterViewInit,
  Directive,
  ElementRef,
  Renderer2,
  ChangeDetectorRef,
  Input,
  SimpleChanges
} from '@angular/core';

import { MediaMatcher } from '@angular/cdk/layout';
import { MatPaginator } from '@angular/material';

@Directive({
  selector: '[appCustomPaginatorStyle]'
})
export class CustomPaginatorStyleDirective implements AfterViewInit {

  private smallDevice: MediaQueryList;
  private mobileQueryListener: () => void;

  @Input() totalItems: number = 0;
  @Input() pageSizeDirective: number;

  @Input() displayLoaderSpinner?: boolean = false;

  private isAddPageLabelAvailable: boolean = false;

  rangeLabelStyles = {
    'font-family': 'Red Hat Display, sans-serif',
    'font-weight': 'var(--font-weight-medium)',
    'font-size': 'var(--font-small)',
    'color': 'var(--secondary-light-10)'
  };

  rangeLabelCreated = {
    'order': 1,
    'margin': '10px'
  }

  secondVersionButtonClass = ['white-theme', 'buttons-button', 'mobile-version'];

  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
    private mediaMatcher: MediaMatcher,
    private changeDetectorRef: ChangeDetectorRef,
    private paginator: MatPaginator
  ) {
    // set breakpoint
    this.smallDevice = mediaMatcher.matchMedia('(max-width: 767.98px)');
    this.mobileQueryListener = () => changeDetectorRef.detectChanges();
    // listener 'change'
    this.smallDevice.addEventListener('change', () => {
      this.addPageLabel(false);
      this.modifyButtons()
    });
  }

  ngAfterViewInit(): void {
    // hide default buttons
    this.modifyPaginator();

    this.addPageLabel(false);

    // subscribe paginator events
    this.paginator.page.subscribe(() => {
      const isBreakpointMatch = this.smallDevice.matches;
      this.addPageLabel(isBreakpointMatch);
    });

    // if is not the requeride breakpoint create first version button, otherwise check and create second version buttons
    if (!this.smallDevice.matches) {
      this.createFirstVersionButton();
    } else {
      this.modifyButtons();
    }
  }

  // listen when totalItems is already ready
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.totalItems && !changes.totalItems.isFirstChange() && this.smallDevice.matches) {
      this.isAddPageLabelAvailable = true;
      this.addPageLabel();
    }

    const rangeLabelLoaderIcon = this.el.nativeElement.querySelector('.range-label-loader-icon');
    if (changes.displayLoaderSpinner && !changes.displayLoaderSpinner.isFirstChange() && !this.smallDevice.matches) {
      if (changes.displayLoaderSpinner.currentValue) {
        this.renderer.removeStyle(rangeLabelLoaderIcon, 'display');
      } else if (!changes.displayLoaderSpinner.currentValue) {
        this.renderer.setStyle(rangeLabelLoaderIcon, 'display', 'none');
      }
    } else if (changes.displayLoaderSpinner && !changes.displayLoaderSpinner.isFirstChange() && this.smallDevice.matches) {
      this.renderer.setStyle(rangeLabelLoaderIcon, 'display', 'none');
    }
  }

  createButtonClasses(theme: string): string[] {
    return ['buttons-button', theme];
  }

  private createButton(button, theme, buttonText) {
    this.renderer.setProperty(button, 'innerText', '');
    this.setClasses(button, this.createButtonClasses(theme));
    const span = this.renderer.createElement('span');
    const spanText = this.renderer.createText(buttonText);
    this.renderer.appendChild(span, spanText);
    this.renderer.appendChild(button, span);
  }

  private createSecondVersionButton(button, iconPath, deg, order = null) {
    this.renderer.setProperty(button, 'innerText', '');
    const img = this.renderer.createElement('img');
    this.renderer.setAttribute(img, 'src', iconPath);
    this.renderer.setStyle(img, 'transform', deg);
    if ( order != null ) {
      this.renderer.setStyle(button, 'order', order);
      this.setClasses(button, this.secondVersionButtonClass);
    }
    this.renderer.appendChild(button, img);
  }

  private createFirstVersionButton() {
    const matPaginatorContainer = this.el.nativeElement.querySelector('.mat-paginator-container');
    this.renderer.addClass(matPaginatorContainer, 'buttons');

    const buttonPrevious = this.el.nativeElement.querySelector('.mat-paginator-navigation-previous');
    if (buttonPrevious) {
      this.createButton(buttonPrevious, 'white-theme', 'Atrás');
    }

    const buttonNext = this.el.nativeElement.querySelector('.mat-paginator-navigation-next');
    if (buttonNext) {
      this.createButton(buttonNext, 'white-theme', 'Siguiente');
    }
  }

  private modifyPaginator() {
    const arrowIcons = this.el.nativeElement.querySelectorAll('.mat-paginator-navigation-last, .mat-paginator-navigation-first');
    arrowIcons.forEach(arrow => this.renderer.setStyle(arrow, 'display', 'none'));
    const rangeLabel = this.el.nativeElement.querySelector('.mat-paginator-range-label');
    this.setStyles(rangeLabel, this.rangeLabelStyles);
    const img = this.renderer.createElement('img');
    this.renderer.setAttribute(img, 'src', '../../assets/new-icons/loader-icon.svg');
    this.renderer.addClass(img, 'range-label-loader-icon');
    this.renderer.appendChild(rangeLabel, img);
  }

  private createrangeLabel(element, text, hide) {
    const rangeLabelCreated = this.el.nativeElement.querySelector('.range-label-created');
    if (rangeLabelCreated) {
      this.renderer.setProperty(rangeLabelCreated, 'innerText', '');
      this.renderer.appendChild(rangeLabelCreated, text);
    } else {
      const div = this.renderer.createElement('div');
      this.renderer.addClass(div, 'range-label-created');
      this.renderer.setStyle(div, 'display', hide ? '' : 'none');
      this.setStyles(div, this.rangeLabelCreated)
      this.renderer.appendChild(div, text);
      this.renderer.appendChild(element, div);
    }
  }

  private modifyButtons() {
    const pageSize = this.el.nativeElement.querySelector('.mat-paginator-page-size');
    const buttonPrevious = this.el.nativeElement.querySelector('.mat-paginator-navigation-previous');
    const buttonNext = this.el.nativeElement.querySelector('.mat-paginator-navigation-next');
    const rangeLabel = this.el.nativeElement.querySelector('.mat-paginator-range-label');
    const matPaginatorContainer = this.el.nativeElement.querySelector('.mat-paginator-container');
    const rangeLabelCreated = this.el.nativeElement.querySelector('.range-label-created');

    if (this.smallDevice.matches) {
      this.isAddPageLabelAvailable = true;

      if (buttonPrevious && buttonNext) {

        if (rangeLabelCreated) {
          this.renderer.removeStyle(rangeLabelCreated, 'display');
        }

        this.renderer.setStyle(matPaginatorContainer, 'justify-content', 'center');
        this.renderer.setStyle(rangeLabel, 'display', 'none');
        this.renderer.setStyle(pageSize, 'display', 'none');

        this.createSecondVersionButton(buttonPrevious, '../../assets/new-icons/arrow-version-2.svg', 'rotate(180deg)', 0);
        this.createSecondVersionButton(buttonNext, '../../assets/new-icons/arrow-version-2.svg', 'rotate(0deg)', 2);
      }
    } else {
      this.isAddPageLabelAvailable = false;
      if (rangeLabelCreated) {
        this.renderer.setStyle(rangeLabelCreated, 'display', 'none');
      }

      this.renderer.setStyle(pageSize, 'display', 'flex')
      this.renderer.removeStyle(rangeLabel, 'display');

      this.renderer.removeStyle(matPaginatorContainer, 'justify-content');

      if (buttonPrevious && buttonNext) {
        this.createButton(buttonPrevious, 'white-theme', 'Atrás');
        this.createButton(buttonNext, 'white-theme', 'Siguiente');
      }
    }
  }

  private addPageLabel(hide = true) {
    const matPaginatorContainer = this.el.nativeElement.querySelector('.mat-paginator-range-actions');
    const pageInfo = `Pág. ${this.paginator.pageIndex + 1} de ${Math.ceil(this.totalItems / this.pageSizeDirective)}`;
    const textNode = this.renderer.createText(pageInfo);
    this.createrangeLabel(matPaginatorContainer, textNode, hide);
  }

  setStyles(element: ElementRef, styles: Record<string, string | number>): void {
    for (const [property, value] of Object.entries(styles)) {
      this.renderer.setStyle(element, property, value);
    }
  }

  setClasses(element: ElementRef, classes: string[]): void {
    classes.map((el) =>this.renderer.addClass(element, el));
  }

  ngOnDestroy() {
    this.smallDevice.removeEventListener('change', this.mobileQueryListener);
  }
}
